import {FormControl, FormGroup} from '@angular/forms';
import {FeedbackAnswerStateResource, FeedbackQuestionType} from '../../../shared/types/generated';
import {AttemptFormValue} from '../../../shared/types/feedback-types/attemptFormValue.type';
import {
	FeedbackStructureQuestionFormQuestion,
} from '../feedback-structure-create-update-dialog/feedback-structure-question/feedback-structure-question-form.model';
import {
	feedbackAnswerControlName,
	feedbackCommentControlName,
	feedbackOptionCommentControlName,
	feedbackOptionControlName,
} from '../helpers/feedback-structure-preview-control-names';
import {
	isOptionSelected,
	optionCommentValue,
	orderingPosition,
	questionAnswerControlValue,
} from './feedback-attempt-answer-mapper';

export function syncAttemptFormControls(
	form: FormGroup,
	questions: FeedbackStructureQuestionFormQuestion[],
	answers: FeedbackAnswerStateResource[],
	controlNamePrefix: string,
	isQuestionLocked: (questionId: number | undefined) => boolean
): void {
	questions.forEach((question) => {
		const answer = answers.find((attemptAnswer) => attemptAnswer.question_id === question.id);
		ensureQuestionControls(form, question, answer, controlNamePrefix);
		ensureControlValue(form, feedbackCommentControlName(question, controlNamePrefix), answer?.comment ?? '');
		if (isQuestionLocked(question.id)) {
			setQuestionControlsDisabled(form, question, controlNamePrefix, true);
		}
	});
}

export function ensureQuestionControls(
	form: FormGroup,
	question: FeedbackStructureQuestionFormQuestion,
	answer: FeedbackAnswerStateResource | undefined,
	controlNamePrefix: string
): void {
	question.options.forEach((option) => {
		if (option.commentable === true) {
			ensureControlValue(
				form,
				feedbackOptionCommentControlName(question, option, controlNamePrefix),
				optionCommentValue(answer, option)
			);
		}
	});

	switch (question.type) {
		case FeedbackQuestionType.MULTI_SELECTION:
			question.options.forEach((option) => ensureControlValue(
				form,
				feedbackOptionControlName(question, option, controlNamePrefix),
				isOptionSelected(answer, option)
			));
			return;
		case FeedbackQuestionType.ORDERING:
			question.options.forEach((option) => ensureControlValue(
				form,
				feedbackOptionControlName(question, option, controlNamePrefix),
				orderingPosition(answer, option)
			));
			return;
		case FeedbackQuestionType.TEXT_INPUT:
			ensureTextInputControlValue(
				form,
				feedbackAnswerControlName(question, controlNamePrefix),
				questionAnswerControlValue(question, answer)
			);
			return;
		case FeedbackQuestionType.SINGLE_SELECTION:
		case FeedbackQuestionType.DROPDOWN:
		case FeedbackQuestionType.SLIDER:
		case FeedbackQuestionType.RATING:
		case FeedbackQuestionType.DATE_TIME:
		case FeedbackQuestionType.TIME:
		case FeedbackQuestionType.DATE:
		case FeedbackQuestionType.FILES:
			ensureControlValue(
				form,
				feedbackAnswerControlName(question, controlNamePrefix),
				questionAnswerControlValue(question, answer)
			);
			return;
	}
}

export function questionControlNames(question: FeedbackStructureQuestionFormQuestion, controlNamePrefix: string): string[] {
	const controlNames = [feedbackCommentControlName(question, controlNamePrefix)];

	switch (question.type) {
		case FeedbackQuestionType.SINGLE_SELECTION:
		case FeedbackQuestionType.DROPDOWN:
		case FeedbackQuestionType.SLIDER:
		case FeedbackQuestionType.RATING:
		case FeedbackQuestionType.TEXT_INPUT:
		case FeedbackQuestionType.DATE_TIME:
		case FeedbackQuestionType.TIME:
		case FeedbackQuestionType.DATE:
		case FeedbackQuestionType.FILES:
			controlNames.push(feedbackAnswerControlName(question, controlNamePrefix));
			break;
		case FeedbackQuestionType.MULTI_SELECTION:
		case FeedbackQuestionType.ORDERING:
			question.options.forEach((option) => controlNames.push(feedbackOptionControlName(question, option, controlNamePrefix)));
			break;
	}

	question.options
		.filter((option) => option.commentable === true)
		.forEach((option) => controlNames.push(feedbackOptionCommentControlName(question, option, controlNamePrefix)));

	return controlNames;
}

export function setQuestionControlsDisabled(
	form: FormGroup,
	question: FeedbackStructureQuestionFormQuestion,
	controlNamePrefix: string,
	disabled: boolean
): void {
	questionControlNames(question, controlNamePrefix).forEach((controlName) => {
		const control = form.get(controlName);
		if (control === null) {
			return;
		}

		if (disabled) {
			control.disable({emitEvent: false});
			return;
		}

		control.enable({emitEvent: false});
	});
}

export function controlValue(form: FormGroup, controlName: string): unknown {
	return form.get(controlName)?.value ?? null;
}

export function markAllControlsTouched(form: FormGroup): void {
	Object.values(form.controls).forEach((control) => control.markAsTouched());
}

export function changedControlNames(previousSnapshot: AttemptFormValue, currentSnapshot: AttemptFormValue): Set<string> {
	const controlNames = new Set([...Object.keys(previousSnapshot), ...Object.keys(currentSnapshot)]);
	return new Set([...controlNames].filter((controlName) =>
		!areControlValuesEqual(previousSnapshot[controlName], currentSnapshot[controlName])
	));
}

export function areControlValuesEqual(firstValue: unknown, secondValue: unknown): boolean {
	return JSON.stringify(firstValue) === JSON.stringify(secondValue);
}

function ensureControlValue(form: FormGroup, controlName: string, value: unknown): void {
	const control = form.get(controlName);
	if (control !== null) {
		if (!areControlValuesEqual(control.value, value)) {
			control.setValue(value, {emitEvent: false});
		}
		return;
	}

	form.addControl(controlName, new FormControl(value));
}

function ensureTextInputControlValue(form: FormGroup, controlName: string, value: unknown): void {
	const control = form.get(controlName);
	if (control === null) {
		form.addControl(controlName, new FormControl(value));
		return;
	}

	// Preserve in-progress typing when autosave responses return older text.
	if (control.dirty) {
		return;
	}

	if (!areControlValuesEqual(control.value, value)) {
		control.setValue(value, {emitEvent: false});
	}
}
